import { useRef, useState } from 'react'

import { createProductRecommendation } from '../components/chat/services/productRecommendationService'
import { createRoutineAdvice } from '../components/chat/services/routineAdviceService'
import { submitChatRecord } from '../components/chat/services/chatRecordService'

import {
  buildRoutineAdvicePayload,
  buildMetadataSubmissionPayload,
} from '../components/chat/utils/recommendationPayloadHelpers'

import {
  addLoadingMessage,
  removeMessageById,
} from '../components/chat/utils/chatMessageHelpers'

import {
  getProductUrl,
  getProductRoutinePrompt,
  getProductDeclinePrompt,
  getProductPageOpenedMessage,
  getContinueExploringMessage,
  getLocalizedProductText,
} from '../components/chat/utils/productHelpers'

import { CHAT_MESSAGES } from '../components/chat/config/chatMessages'
import { toCleanBase64 } from '../components/chat/utils/imageHelpers'


const useProductFlow = ({
  productRecommendations,
  selectedProduct,
  diagnosticsLLMText,
  productRoutineData,
  agentData,
  agentId,
  buildRecommendationPayload,

  getAnswersMap,

  getMessageId,
  getTime,
  addAiMessage,
  addUserMessage,
  messagesRef,
  dispatch,
  setMessages,
  setProductsReady,
  setProductsLoading,
  setProductFlowStep,
  setSelectedProduct,
  setRoutineOpen,
  setRoutineReady,
  setRoutineData,
  resetChat,
  goToAuthorisation,
  translations = {},
  language = 'EN',
  consentData,
  segmentedRawImage
}) => {
  const [
    productRoutineLoading,
    setProductRoutineLoading,
  ] = useState(false)

  const recommendationRequestInFlight = useRef(false)

  // =====================================================
  // FLOW HELPERS
  // =====================================================

  const getCurrentAnswers = () =>
    getAnswersMap?.() || {}

  const getIsGuestFlow = () => {
    const answersMap = getCurrentAnswers()

    return (
      answersMap.existingUserAction ===
      'option_someone_else'
    )
  }

  // =====================================================
  // FETCH PRODUCT RECOMMENDATIONS
  // =====================================================

  const fetchRecommendations = ({
    force = false,
  } = {}) => {
    if (recommendationRequestInFlight.current) {
      return
    }

    if (
      !force &&
      productRecommendations?.product_name?.length > 0
    ) {
      dispatch(setProductsReady(true))
      return
    }

    recommendationRequestInFlight.current = true

    dispatch(setProductsLoading(true))

    const loadingMessageId = getMessageId()

    const messagesWithLoading = addLoadingMessage({
      messages: messagesRef.current,
      loadingMessageId,
      getTime,
    })

    messagesRef.current = messagesWithLoading

    dispatch(
      setMessages(messagesWithLoading),
    )

    const recommendationPayload =
      buildRecommendationPayload()

    console.log(
    'CREATE PRODUCT RECOMMENDATION PAYLOAD:',
    
      recommendationPayload
    )

  console.log(
    'FRONTEND LANGUAGE:',
    language,
  )

  console.log(
    'ANSWERS MAP:',
    getAnswersMap?.(),
  )

    createProductRecommendation(
      recommendationPayload,
    )
      .then((response) => {
      console.log(
      '================ PRODUCT API RESPONSE ================',
    )

    console.log(
      'FULL RESPONSE:',
      response,
    )

        const messagesWithoutLoading =
          removeMessageById({
            messages: messagesRef.current,
            messageId: loadingMessageId,
          })

        messagesRef.current =
          messagesWithoutLoading

        dispatch(
          setMessages(messagesWithoutLoading),
        )

        if (
          response?.price_filtered?.length > 0
        ) {
          dispatch({
            type: 'SET_PRODUCT_RECOMMENDATIONS',
            payload: response,
          })

          dispatch(
            setProductsReady(true),
          )

          const isGuestFlow =
            getIsGuestFlow()

          addAiMessage({
            text: isGuestFlow
              ? 'Here are their personalised product recommendations! You can also view their full diagnostics using the button below.'
              : getLocalizedProductText({
                  translations,
                  language,
                  keyPath:
                    'chatMessages.recommendationReady',
                  fallback:
                    CHAT_MESSAGES.recommendationReady,
                }),

            showSlider: true,
          })

          return
        }

        const isGuestFlow =
          getIsGuestFlow()

        addAiMessage({
          text: isGuestFlow
            ? 'I wasn’t able to find suitable product recommendations for them right now. Please try again in a moment.'
            : getLocalizedProductText({
                translations,
                language,
                keyPath:
                  'chatMessages.recommendationUnavailable',
                fallback:
                  CHAT_MESSAGES.recommendationUnavailable,
              }),
        })
      })
      .catch((error) => {
        console.error(
          'Product recommendation error:',
          error,
        )

        const messagesWithoutLoading =
          removeMessageById({
            messages: messagesRef.current,
            messageId: loadingMessageId,
          })

        messagesRef.current =
          messagesWithoutLoading

        dispatch(
          setMessages(messagesWithoutLoading),
        )

        const isGuestFlow =
          getIsGuestFlow()

        addAiMessage({
          text: isGuestFlow
            ? 'Something went wrong while preparing their product recommendations. Please try again.'
            : getLocalizedProductText({
                translations,
                language,
                keyPath:
                  'chatMessages.recommendationFailed',
                fallback:
                  CHAT_MESSAGES.recommendationFailed,
              }),
        })
      })
      .finally(() => {
        recommendationRequestInFlight.current =
          false

        dispatch(
          setProductsLoading(false),
        )
      })
  }

  // =====================================================
  // PRODUCT SELECTION
  // =====================================================

  const handleProductSelect = (product) => {
    dispatch(
      setSelectedProduct(product),
    )

    dispatch(
      setRoutineReady(false),
    )

    dispatch(
      setRoutineData(null),
    )

    dispatch(
      setProductFlowStep('routine_prompt'),
    )

    const isGuestFlow =
      getIsGuestFlow()

    addAiMessage({
      text: isGuestFlow
        ? `Would you like me to build a personalised routine for them around ${product.name}?`
        : getProductRoutinePrompt(
            product,
            translations,
            language,
          ),
    })
  }

  // =====================================================
  // METADATA / DATABASE SUBMISSION
  //
  // IMPORTANT:
  // Never save guest analysis to primary user's DB.
  // =====================================================

  const handleMetadataSubmission =
    async () => {
      const isGuestFlow =
        getIsGuestFlow()  
      


      if (isGuestFlow) {
        setProductRoutineLoading(false)

        return {
          skipped: true,
          reason: 'guest_flow',
        }
      }

      const answersMap = getAnswersMap?.() || {}

      const productPrice =
        selectedProduct?.price ??
        selectedProduct?.product_price ??
        selectedProduct?.price_value ??
        selectedProduct?.selling_price ??
        null

      const hairMask =
        answersMap?.hairMask ??
        answersMap?.hair_mask ??
        answersMap?.segmentedHairMask ??
        answersMap?.segmented_hair_mask ??
        agentData?.hair_mask ??
        null

      const recommendationPayload =
        buildRecommendationPayload()

      const payload =
        buildMetadataSubmissionPayload({
          recommendationPayload,
          selectedProduct,
          diagnosticsLLMText,
          productRoutineData,
          agentId,
          timestamp: getTime(),
          agentData,
          consentData,
          phoneNumber:
          answersMap?.phoneNumber ||
          agentData?.phone_number ||
          null,
          productPrice,
          hairMask: toCleanBase64(segmentedRawImage),
        })

      try {
        return await submitChatRecord(
          payload,
        )
      } catch (error) {
        addAiMessage({
          text: getLocalizedProductText({
            translations,
            language,
            keyPath:
              'chatMessages.metadataSubmissionFailed',
            fallback:
              CHAT_MESSAGES.metadataSubmissionFailed,
          }),
        })

        return null
      } finally {
        setProductRoutineLoading(false)
      }
    }

  // =====================================================
  // BUILD ROUTINE
  // =====================================================

  const handleViewProductRoutine =
    async () => {
      if (!selectedProduct) return

      setProductRoutineLoading(true)

      dispatch(
        setRoutineOpen(true),
      )

      try {
        const recommendationPayload =
          buildRecommendationPayload()

        const routineAdvicePayload =
          buildRoutineAdvicePayload({
            recommendationPayload,
            selectedProduct,
            diagnosticsLLMText,
            language,
          })

        const response =
          await createRoutineAdvice(
            routineAdvicePayload,
          )

        const routineText =
          response?.routine_llm_output
            ? response.routine_llm_output
                .replace(/^\s*#{1,6}\s*/gm, '')
                .replace(/\*\*/g, '')
                .trim()
            : ''

        dispatch(
          setRoutineData(routineText),
        )

        dispatch(
          setRoutineReady(true),
        )

        dispatch(
          setProductFlowStep('buy_prompt'),
        )

        const isGuestFlow =
          getIsGuestFlow()

        addAiMessage({
          text: isGuestFlow
            ? `Here is a personalised routine built for them around ${selectedProduct.name}. Would you like to view this product?`
            : getLocalizedProductText({
                translations,
                language,
                keyPath:
                  'productFlow.routineBuilt',
                replacements: {
                  productName:
                    selectedProduct.name,
                },
                fallback:
                  `Here is a routine built around ${selectedProduct.name}. Would you like to buy this product?`,
              }),
        })
      } catch (error) {
        const isGuestFlow =
          getIsGuestFlow()

        addAiMessage({
          text: isGuestFlow
            ? 'Something went wrong while preparing their personalised routine. Please try again.'
            : getLocalizedProductText({
                translations,
                language,
                keyPath:
                  'chatMessages.routineFailed',
                fallback:
                  CHAT_MESSAGES.routineFailed,
              }),
        })
      } finally {
        setProductRoutineLoading(false)
      }
    }

  // =====================================================
  // BUY DECISION
  // =====================================================

  const handleBuyDecision =
    async (decision) => {
      if (!selectedProduct) return

      const isGuestFlow =
        getIsGuestFlow()

      if (decision === 'yes') {
        await handleMetadataSubmission()

        addUserMessage({
          text: isGuestFlow
            ? 'Yes, view the product'
            : getLocalizedProductText({
                translations,
                language,
                keyPath:
                  'productFlow.buyYes',
                fallback:
                  'Yes, buy it',
              }),
        })

        const productUrl =
          selectedProduct.official_url ||
          selectedProduct.product_url ||
          selectedProduct.url

        if (productUrl) {
          window.open(
            productUrl,
            '_blank',
            'noopener,noreferrer',
          )
        }

        dispatch(
          setProductFlowStep(null),
        )

        return
      }

      await handleMetadataSubmission()

      addUserMessage({
        text: getLocalizedProductText({
          translations,
          language,
          keyPath: 'productFlow.buyNo',
          fallback: 'No',
        }),
      })

      addAiMessage({
        text: isGuestFlow
          ? `No problem. You can continue exploring other options for them, or choose another recommendation.`
          : getProductDeclinePrompt(
              selectedProduct,
              translations,
              language,
            ),
      })

      dispatch(
        setProductFlowStep(
          'decline_reason',
        ),
      )
    }

  // =====================================================
  // BUY / VIEW PRODUCT CLICK
  // =====================================================

  const handleBuyClick = () => {
    const isGuestFlow =
      getIsGuestFlow()

    handleMetadataSubmission()

    addUserMessage({
      text: isGuestFlow
        ? 'Yes, view the product'
        : getLocalizedProductText({
            translations,
            language,
            keyPath:
              'productFlow.buyYesDetailed',
            fallback:
              'Yes, I want to buy it',
          }),
    })

    addAiMessage({
      text: isGuestFlow
        ? 'The product page has been opened. You can continue exploring their recommendations here whenever you’re ready.'
        : getProductPageOpenedMessage(
            translations,
            language,
          ),
    })

    dispatch(
      setProductFlowStep('next_action'),
    )

    const productUrl =
      getProductUrl(selectedProduct)

    if (productUrl) {
      window.open(
        productUrl,
        '_blank',
        'noopener,noreferrer',
      )
    }
  }

  // =====================================================
  // CONTINUE EXPLORING
  // =====================================================

  const handleContinueExploring = () => {
    const isGuestFlow =
      getIsGuestFlow()

    addUserMessage({
      text: getLocalizedProductText({
        translations,
        language,
        keyPath:
          'productFlow.continueExploringAction',
        fallback: 'Continue exploring',
      }),
    })

    addAiMessage({
      text: isGuestFlow
        ? 'Of course. You can continue exploring their personalised product recommendations.'
        : getContinueExploringMessage(
            translations,
            language,
          ),
    })

    dispatch(
      setProductFlowStep(null),
    )
  }

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogOut = () => {
    addUserMessage({
      text: getLocalizedProductText({
        translations,
        language,
        keyPath:
          'productFlow.logOutAction',
        fallback: 'Log out',
      }),
    })

    dispatch(resetChat())

    goToAuthorisation()
  }

  // =====================================================
  // EXPORTS
  // =====================================================

  return {
    productRoutineLoading,
    fetchRecommendations,
    handleProductSelect,
    handleMetadataSubmission,
    handleViewProductRoutine,
    handleBuyDecision,
    handleBuyClick,
    handleContinueExploring,
    handleLogOut,
  }
}

export default useProductFlow